import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TableCell,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Visibility, Search as SearchIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import AccountantLayout from '../components/AccountantLayout';
import PaginatedTable from '../components/PaginatedTable';

const BankingPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadTransactions();
  }, [page, rowsPerPage, searchQuery]);

  const loadTransactions = async () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      const mockTransactions = [
        { id: 1, date: '2026-06-28', description: 'Payment from Atlas Construction', amount: 25000, type: 'Credit', account: 'Main Account' },
        { id: 2, date: '2026-06-27', description: 'Supplier Payment - Office Supplies', amount: -1250, type: 'Debit', account: 'Main Account' },
        { id: 3, date: '2026-06-26', description: 'Payment from Meridian Logistics', amount: 18500, type: 'Credit', account: 'Main Account' },
        { id: 4, date: '2026-06-25', description: 'Rent Payment', amount: -3500, type: 'Debit', account: 'Main Account' },
        { id: 5, date: '2026-06-24', description: 'Payment from Orchard Foods', amount: 32000, type: 'Credit', account: 'Savings Account' },
      ];
      setTransactions(mockTransactions);
      setTotalCount(mockTransactions.length);
      setLoading(false);
    }, 500);
  };

  const columns = [
    { id: 'date', label: t('date'), minWidth: 120 },
    { id: 'description', label: t('description'), minWidth: 250 },
    { id: 'type', label: t('type'), minWidth: 100 },
    { id: 'amount', label: t('amount'), minWidth: 140, align: 'right' },
    { id: 'account', label: t('account'), minWidth: 150 },
    { id: 'actions', label: t('actions'), minWidth: 80, align: 'center' },
  ];

  const renderRow = (transaction) => (
    <TableRow hover key={transaction.id}>
      <TableCell>{transaction.date}</TableCell>
      <TableCell>{transaction.description}</TableCell>
      <TableCell>
        <Chip
          label={transaction.type}
          size="small"
          sx={{
            backgroundColor: transaction.type === 'Credit' ? '#E8F5E9' : '#FFEBEE',
            color: transaction.type === 'Credit' ? '#4CAF50' : '#F44336',
            fontWeight: 500,
          }}
        />
      </TableCell>
      <TableCell align="right" sx={{ color: transaction.amount > 0 ? '#4CAF50' : '#F44336', fontWeight: 500 }}>
        €{Math.abs(transaction.amount).toLocaleString()}
      </TableCell>
      <TableCell>{transaction.account}</TableCell>
      <TableCell align="center">
        <IconButton size="small" color="primary">
          <Visibility fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );

  return (
    <AccountantLayout>
      <Box>
        <Box className="accountant-page-header" sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {t('accountant_banking')}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {t('manage_bank_transactions')}
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder={t('search_transactions_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: 'white',
              borderRadius: '8px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />
        </Box>

        <PaginatedTable
          columns={columns}
          data={transactions}
          loading={loading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          emptyMessage={t('no_transactions_found')}
          renderRow={renderRow}
        />
      </Box>
    </AccountantLayout>
  );
};

export default BankingPage;
